import type { CfFailureReason } from '@prerender/prerender-shared/FE';
import { CfFlowResponse } from './api/cloudflareFlowApi';

export type CfFailureCopy = { message: string; action: string; statesTheUndo?: boolean };

export const CF_REMOVE_LEFTOVERS_ACTION =
  'Remove what we left in your Cloudflare account, then ask Nexus to help you finish the setup.';

const RETRY_SSL = 'Set SSL/TLS to Full or Automatic, then choose Retry Setup.';

const FAILURE_COPY: Record<CfFailureReason, (domain: string) => CfFailureCopy> = {
  ssl_unsafe: () => ({
    message: 'Your Cloudflare SSL setting could break your site.',
    action: RETRY_SSL,
  }),
  ssl_unconfirmed: () => ({
    message: "We couldn't confirm your Cloudflare SSL setting is safe.",
    action: RETRY_SSL,
  }),
  route_collision: () => ({
    message: 'Another Cloudflare Worker route already covers this domain.',
    action: 'Remove or adjust that route, then choose Retry Setup.',
  }),
  zone_not_active: () => ({
    message: "Your domain isn't active in Cloudflare yet.",
    action: 'Once it shows as active, choose Retry Setup.',
  }),
  workers_not_enabled: () => ({
    message: "We couldn't turn on Cloudflare Workers for your account.",
    action: 'Open the Workers section once in Cloudflare, then choose Retry Setup.',
  }),
  token_missing_permissions: () => ({
    message: 'Your Cloudflare token is missing permissions we need to finish setup.',
    action:
      'Create a new Cloudflare token with the required permissions, then ask Nexus to help you reconnect it.',
  }),
  token_rejected: () => ({
    message: "Cloudflare didn't accept your token.",
    action: 'Create a new Cloudflare token, then ask Nexus to help you reconnect it.',
  }),
  zone_not_found: (domain) => ({
    message: `Your Cloudflare token can't see ${domain}.`,
    action:
      'Check that the domain is in the same Cloudflare account as the token, then choose Retry Setup.',
  }),
  dns_unreadable: (domain) => ({
    message: `We couldn't read the DNS records for ${domain}.`,
    action: 'Choose Retry Setup. If it happens again, ask Nexus.',
  }),
  site_unreachable_after_proxy: () => ({
    message:
      'After turning on Cloudflare proxying, your site stopped responding, so we undid the change to keep it online.',
    action: 'Check that your site is reachable, then choose Retry Setup.',
    statesTheUndo: true,
  }),
  origin_unknown: (domain) => ({
    message: "We couldn't tell where one of your domain's DNS records should point.",
    action: `Check the A or CNAME records for ${domain} and www.${domain} in Cloudflare, then choose Retry Setup.`,
  }),
  worker_limit_reached: () => ({
    message: 'Your Cloudflare account has reached its Worker limit. We never delete your Workers.',
    action:
      'Delete a Worker you no longer use or upgrade your Cloudflare plan, then choose Retry Setup.',
  }),
  workers_subdomain_missing: () => ({
    message: "Your Cloudflare account doesn't have a workers.dev subdomain yet.",
    action: 'Open Workers & Pages once in Cloudflare to create it, then choose Retry Setup.',
  }),
  rate_limited: () => ({
    message: 'Cloudflare is limiting requests from your account right now.',
    action: 'Wait a few minutes, then choose Retry Setup.',
  }),
  cloudflare_unreachable: () => ({
    message: "We couldn't reach Cloudflare.",
    action: 'Wait a minute, then choose Retry Setup.',
  }),
  prerender_token_missing: () => ({
    message: "We couldn't find your Prerender token, so we can't finish the setup.",
    action: 'Contact support.',
  }),
  setup_interrupted: () => ({
    message: "Prerender couldn't deploy the worker to Cloudflare.",
    action: 'Nexus can help you troubleshoot, or you can follow our step-by-step guides.',
  }),
  setup_failed: () => ({
    message: 'Something went wrong on our side while setting up Cloudflare.',
    action: 'Choose Retry Setup. If it happens again, ask Nexus or contact support.',
  }),
};

const isKnownFailureReason = (reason: string | undefined): reason is CfFailureReason =>
  reason !== undefined && Object.prototype.hasOwnProperty.call(FAILURE_COPY, reason);

export const cfFailureCopyFor = (
  reason: string | undefined,
  domain: string,
  rollbackOutcome?: CfFlowResponse['rollbackOutcome'],
): CfFailureCopy => {
  const copy = FAILURE_COPY[isKnownFailureReason(reason) ? reason : 'setup_failed'](domain);

  return rollbackOutcome === 'failed' ? { ...copy, action: CF_REMOVE_LEFTOVERS_ACTION } : copy;
};
